import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { Reveal } from "@/components/landing/Reveal";
import { scrollToId } from "@/lib/nav";
import { LIBRARY_SECTIONS, LIBRARY_KINDS } from "@/data/library";

export default function LibraryPage() {
  const [active, setActive] = useState(LIBRARY_SECTIONS[0].id);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-30% 0px -55% 0px" }
    );
    LIBRARY_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <main data-testid="library-page">
      <Seo
        title="Marian Library — the Church's own documents on the Mother of God · Theotokos · Mater Dei"
        siteName="Theotokos · Mater Dei"
        description="From the Sub Tuum Praesidium of the 3rd century to the 2024 DDF Norms on apparitions — a curated library of the Church's own documents on the Blessed Virgin Mary, with excerpts and links to the official sources."
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "Discover", to: "/apologetics" }, { label: "Library" }]}
        eyebrow="Discover · Library"
        lines={["The Church's", "own words"]}
        aside={
          <div className="rounded-2xl border border-marian/15 bg-white/70 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">A shelf, not an argument</p>
            <p className="mt-2 font-serif text-lg leading-snug text-midnight">
              We publish nothing on the Mother of God that the Church has not said herself.
            </p>
            <p className="mt-2 text-xs text-slateblue">Read her here in her own voice — Councils, Pontiffs, Roman Dicasteries.</p>
          </div>
        }
      >
        Every doctrine, prayer, and discernment on this site rests on documents anyone can verify.
        These are the most important of them — from the oldest Marian prayer we still have to
        the norms issued in May 2024. Each has a short note on why it matters and a link to the
        official text.
      </PageIntro>

      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-8 lg:grid-cols-[16rem_minmax(0,1fr)] lg:px-16">
        <nav aria-label="Library sections" className="hidden lg:block">
          <ol className="sticky top-32 space-y-1">
            {LIBRARY_SECTIONS.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  data-testid={`library-toc-${s.id}`}
                  onClick={() => scrollToId(s.id)}
                  className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${active === s.id ? "bg-paleblue text-midnight" : "text-slateblue hover:text-marian"}`}
                >
                  <span className="mt-0.5 font-mono text-[10px] text-gold">0{i + 1}</span>
                  <span>
                    <span className="block font-semibold">{s.label}</span>
                    <span className="mt-0.5 block text-[10px] uppercase tracking-[0.2em] text-slateblue">{s.kicker}</span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="min-w-0 space-y-16" data-testid="library-body">
          {LIBRARY_SECTIONS.map((section, i) => (
            <section
              key={section.id}
              id={section.id}
              className="scroll-mt-28"
              data-testid={`library-section-${section.id}`}
            >
              <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Chapter 0{i + 1} · {section.kicker}</p>
                <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">{section.label}</h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-charcoal/85">{section.intro}</p>
              </Reveal>

              <ol className="mt-8 space-y-6" data-testid={`library-entries-${section.id}`}>
                {section.entries.map((e, ix) => (
                  <Reveal key={e.id} delay={ix * 0.05}>
                    <li
                      id={e.id}
                      data-testid={`library-entry-${e.id}`}
                      className="scroll-mt-28 overflow-hidden rounded-3xl border border-marian/15 bg-white/95 shadow-[0_18px_40px_-30px_rgba(16,42,67,0.35)]"
                    >
                      <div className="gold-rule" />
                      <div className="grid gap-6 p-6 sm:grid-cols-[auto_1fr] sm:p-8">
                        <div className="sm:w-40">
                          <span className={`inline-flex rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] ${LIBRARY_KINDS[e.kind] || "bg-charcoal/10 text-charcoal"}`}>
                            {e.kind}
                          </span>
                          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-slateblue">{e.year}</p>
                          <p className="mt-2 font-serif text-sm italic leading-snug text-slateblue">{e.author}</p>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-serif text-2xl font-semibold text-midnight sm:text-3xl">{e.title}</h3>
                          <p className="mt-3 text-sm leading-relaxed text-charcoal/85 sm:text-base">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Why it matters — </span>
                            {e.why}
                          </p>
                          <div data-testid={`library-summary-${e.id}`} className="mt-5 border-l-2 border-gold/70 pl-4">
                            <p className="font-serif text-base italic leading-relaxed text-midnight sm:text-lg">{e.excerpt}</p>
                            <p data-testid={`library-citation-${e.id}`} className="mt-3 text-xs leading-relaxed text-slateblue">Editorial summary · {e.citation}</p>
                          </div>
                          <a
                            href={e.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`library-entry-link-${e.id}`}
                            className="group mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-marian transition-colors hover:text-midnight"
                          >
                            Read the source
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.24em] text-slateblue">
                              {e.urlLabel}
                            </span>
                          </a>
                        </div>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ol>
            </section>
          ))}

          <Reveal>
            <div className="mt-8 rounded-3xl border border-marian/15 bg-paleblue/40 p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Editorial note</p>
              <p className="mt-3 font-serif text-2xl leading-snug text-midnight sm:text-3xl">
                See how each verdict shapes what we publish — from a black-line dogma to a green-line pilgrimage — on our{" "}
                <Link to="/editorial-standards" data-testid="library-to-standards" className="text-marian underline decoration-gold decoration-2 underline-offset-4 hover:text-midnight">
                  Editorial Standards page
                </Link>
                .
              </p>
              <Link
                to="/apologetics"
                data-testid="library-to-apologetics"
                className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-marian hover:text-midnight"
              >
                Or read the Marian Apologetics chapter
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
